import React, { useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import PropTypes from 'prop-types';
import './tooltip.scss';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';
import { getUUID } from '../../../utils/general-utils';

const TooltipThemes = {
  DARK: 'theme-dark',
  LIGHT: 'theme-light',
};

const TooltipTriggers = {
  CLICK: 'click',
  HOVER: 'hover',
};

const TooltipPlacements = {
  AUTO: 'auto',
  TOP: 'top',
  BOTTOM: 'bottom',
  RIGHT: 'right',
  LEFT: 'left',
};

const hoverShowEvents = ['mouseenter', 'focus'];

const hoverHideEvents = ['mouseleave', 'blur'];

const TransitionDuration = 200;

const Tooltip = ({
  title, description, items, trigger, placement, theme, children, enterDelay, leaveDelay, className,
}) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const popperInstance = useRef(null);
  const enterTimer = useRef();
  const leaveTimer = useRef();
  const [open, setOpen] = useState(false);
  const uuidMarker = useRef(getUUID());

  popperInstance.current = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'arrow',
        options: { element: arrowElement },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
    placement,
    strategy: 'fixed',
  });

  const getUUIDMarker = () => uuidMarker.current;

  const handleOpen = () => {
    setOpen(true);
    // popperElement.setAttribute('data-show', '');
    popperInstance.current.update();
  };

  const handleClose = () => {
    setOpen(false);
    // popperElement.removeAttribute('data-show');
  };

  const handleEnter = (event) => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);

    if (enterDelay) {
      enterTimer.current = setTimeout(
        () => {
          handleOpen(event);
        },
        enterDelay,
      );
    } else {
      handleOpen(event);
    }
  };

  const handleLeave = (event) => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      handleClose(event);
    }, leaveDelay);
  };

  const handleReferenceClicked = (event) => {
    // eslint-disable-next-line no-param-reassign,no-underscore-dangle
    event._selfClicked_ = getUUIDMarker();
    handleOpen();
  };

  const handleDocumentClick = (event) => {
    // eslint-disable-next-line no-underscore-dangle
    if (event.target !== referenceElement && event._selfClicked_ !== getUUIDMarker()) {
      handleClose(event);
    }
  };

  useEffect(() => {
    if (referenceElement && popperElement) {
      if (trigger === TooltipTriggers.HOVER) {
        hoverShowEvents.forEach((event) => {
          referenceElement.addEventListener(event, handleEnter);
        });

        hoverHideEvents.forEach((event) => {
          referenceElement.addEventListener(event, handleLeave);
        });
      } else if (trigger === TooltipTriggers.CLICK) {
        referenceElement.addEventListener('click', handleReferenceClicked);
        document.addEventListener('click', handleDocumentClick);
      }
    }
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [referenceElement, popperElement]);

  const childrenProps = {
    ...children?.props,
    ref: setReferenceElement,
  };

  const getTitle = (text) => (text?.trim()
    ? <div className="tooltip-title">{text}</div> : null);

  const getDescription = (text) => (text?.trim()
    ? <div className="tooltip-description">{text}</div> : null);

  const getItemView = ({ title: titleText, description: descriptionText }, index) => (
    <div className="tooltip-item-cont" key={String(index)}>
      {getTitle(titleText)}
      {getDescription(descriptionText)}
    </div>
  );

  const getBody = () => {
    if (items?.length) {
      return items?.map((item, index) => getItemView(item, index));
    }
    return (
      <>
        {getTitle(title)}
        {getDescription(description)}
      </>
    );
  };

  const getPopperElement = () => (
    <CSSTransition
      nodeRef={{ current: popperElement }}
      in={open}
      timeout={TransitionDuration}
      classNames="tp-trans"
    >
      <div
        className={clsx('tooltip-popper', theme, trigger, className)}
        ref={setPopperElement}
        style={popperInstance?.current?.styles?.popper}
        data-show={open}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...popperInstance?.current?.attributes?.popper}
      >
        {getBody()}
        <div
          className="tooltip-arrow"
          ref={setArrowElement}
          style={popperInstance?.current?.styles?.arrow}
        />
      </div>
    </CSSTransition>
  );

  return (
    <>
      {children ? React.cloneElement(children, childrenProps) : null}
      {getPopperElement()}
    </>
  );
};

Tooltip.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  })),
  trigger: PropTypes.oneOf(Object.values(TooltipTriggers)),
  placement: PropTypes.oneOf(Object.values(TooltipPlacements)),
  theme: PropTypes.oneOf(Object.values(TooltipThemes)),
  children: PropTypes.element.isRequired,
  enterDelay: PropTypes.number,
  leaveDelay: PropTypes.number,
  className: PropTypes.string,
};

Tooltip.defaultProps = {
  title: '',
  description: '',
  items: [],
  trigger: TooltipTriggers.HOVER,
  placement: TooltipPlacements.BOTTOM,
  theme: TooltipThemes.LIGHT,
  enterDelay: 100,
  leaveDelay: 0,
  className: '',
};

export {
  Tooltip as default,
  TooltipThemes,
  TooltipPlacements,
  TooltipTriggers,
};
